import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetBookingDetailsQuery } from '../api/booking/bookingApiSlice';
import { setBookingDetails } from '../store/slices/bookingCreationSlice';
import { RootState } from '../store/store';
import { hasBookingDetailsForOrder } from '../utils/bookingDetailsForOrder';

/**
 * The booking detail for the order in the URL, for the screens that render it
 * out of the legacy `bookingCreation` store — venue and apartment/room, which
 * are otherwise near-identical files and had a copy of all three steps each.
 *
 * 1. **Subscribes** to the query `BookingDetailsPage` already made for this
 *    order — same args, same cache entry, so a detail page still costs one
 *    `GET /order/:id/room-rental`. Both screens used to run a second, lazy
 *    query of their own and gate their whole body on *its* `isFetching`, which
 *    could sit at `true` after the shared request had already fulfilled: a
 *    spinner that never resolved on a booking whose payload was in hand.
 * 2. **Seeds** the legacy store the bodies render from.
 * 3. Answers **"may I render?"** — `showBookingDetails` asks whether we hold
 *    the payload for THIS order, never whether a request is in flight, so a
 *    background refetch cannot blank a rendered page and the previously opened
 *    booking cannot appear under the new order's URL.
 *
 * `refetch` is the post-mutation refresh (setup-time change, cancel).
 *
 * The non-room screen deliberately does NOT use this: it renders from the
 * legacy bookings list rather than from `bookingCreation.bookingDetails`, and
 * seeding a store it never wrote would change what other screens read.
 */
export const useBookingDetailsForOrder = (orderId: string) => {
    const dispatch = useDispatch();
    const { data, refetch } = useGetBookingDetailsQuery({ orderId });
    const bookingDetails = useSelector(
        (state: RootState) => state.bookingCreation.bookingDetails,
    );

    useEffect(() => {
        if (data) {
            dispatch(setBookingDetails(data));
        }
    }, [data, dispatch]);

    return {
        data,
        refetch,
        bookingDetails,
        showBookingDetails: hasBookingDetailsForOrder(bookingDetails, orderId),
    };
};
